import React, { useMemo, useState } from 'react';
import { RowSelectionState } from '@tanstack/react-table';
import { Select } from 'antd';
import { useNotificationContext } from '@/features/notifications';
import {
  ConfluenceContentItemDto,
  UploadConfluenceFilesReqBodyDto,
  useListConfluenceSites,
  useListConfluenceSiteSpaces,
  useListConfluenceSpacePages,
  useListConfluencePageContent,
  useUploadConfluenceFilesToOpenAI,
  UploadConcluenceFileItemDto,
  ListConfluenceSiteSpacesQueryParams,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import {
  FileExplorerActions,
  FileExplorerBreadCrumb,
  FileExplorerTable,
} from '@/shared/ui/file-explorer';
import { captureError } from '@/shared/utils/error-reporting';

type ConfluenceSpaceType = ListConfluenceSiteSpacesQueryParams['spaceType'];

interface ConfluenceFileExplorerProps {
  onFilesAttach: () => void;
  isConnected?: boolean;
}

const spacesTypeOptions = [
  { label: 'Global', value: 'global' },
  { label: 'Knowledge Base', value: 'knowledge_base' },
];

const initialFolder = {
  id: 'sites',
  name: 'List of Sites',
} as ConfluenceContentItemDto;

const getFilesUploadBody = (
  rowSelection: RowSelectionState,
  data: ConfluenceContentItemDto[] = [],
): UploadConfluenceFilesReqBodyDto => ({
  files: data?.reduce(
    (acc, { id, type }) => (rowSelection[id] ? [...acc, { id, type }] : acc),
    [] as UploadConcluenceFileItemDto[],
  ),
});

export function ConfluenceFileExplorer({
  isConnected,
  onFilesAttach,
}: ConfluenceFileExplorerProps) {
  const { activeOrganization } = useRouterContext();
  const { showNotification } = useNotificationContext();
  const [spaceType, setSpaceType] = useState<ConfluenceSpaceType>('global');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [folders, setFolders] = useState([initialFolder]);
  const [, currentSite, currentSpace] = folders;
  const currentFolder = folders[folders.length - 1];
  const queryParams = {
    siteId: currentSite?.id,
    spaceId: currentSpace?.id,
    pageId: currentFolder.id,
  };

  const { data: sitesData, isFetching: loadingSites } = useListConfluenceSites(
    {},
    { enabled: isConnected },
  );
  const { data: spacesData, isFetching: loadingSpaces } =
    useListConfluenceSiteSpaces(
      { queryParams: { siteId: queryParams.siteId, spaceType } },
      { enabled: currentFolder.type === 'site' },
    );
  const { data: pagesData, isFetching: loadingPages } =
    useListConfluenceSpacePages(
      { queryParams },
      { enabled: currentFolder.type === 'space' },
    );
  const { data: contentData, isFetching: loadingContent } =
    useListConfluencePageContent(
      { queryParams },
      { enabled: currentFolder.type === 'page' },
    );

  const { mutate: uploadFiles, isLoading: uploadingFiles } =
    useUploadConfluenceFilesToOpenAI({
      onSuccess: async () => {
        onFilesAttach();
        showNotification({
          type: 'success',
          message: 'Success',
          description: 'Files attached successfully',
        });
      },
      onError: (error) => {
        captureError(error, {
          module: 'knowledge_base',
          action: 'attach_files',
          metadata: { organizationId: activeOrganization?.id },
        });
        showNotification({
          type: 'error',
          message: 'Error attaching files',
          description: error?.message || 'Failed to attach files',
        });
      },
    });

  const loading =
    loadingSites ||
    loadingSpaces ||
    loadingPages ||
    loadingContent ||
    uploadingFiles;
  const displayData = useMemo(() => {
    if (currentFolder.type === 'page') {
      return contentData?.content;
    }
    if (currentFolder.type === 'space') {
      return pagesData?.pages;
    }
    if (currentFolder.type === 'site') {
      return spacesData?.spaces;
    }
    return sitesData?.sites;
  }, [currentFolder.type, pagesData, spacesData, sitesData, contentData]);

  return (
    <>
      <FileExplorerBreadCrumb
        items={folders}
        loading={loading}
        onSelect={(_, __, nextFolders) => setFolders(nextFolders)}
      />
      {currentFolder.type === 'site' && (
        <Select<ConfluenceSpaceType>
          style={{ marginBottom: 16, width: '100%' }}
          placeholder="Space type"
          options={spacesTypeOptions}
          value={spaceType}
          onSelect={setSpaceType}
        />
      )}
      <FileExplorerTable
        data={displayData}
        loading={loading}
        rowSelection={rowSelection}
        onRowSelect={setRowSelection}
        onRowClick={(row) => setFolders([...folders, row])}
        isRowClickable={({ type }) => type !== 'attachment'}
        enableRowSelection={({ original }) => original.type !== 'space'}
        showRowsSelection
      />
      <FileExplorerActions
        showNavActions={false}
        loading={loading}
        canSubmit={!!Object.values(rowSelection).length}
        onSubmit={() =>
          uploadFiles({
            queryParams,
            body: getFilesUploadBody(rowSelection, displayData),
          })
        }
      />
    </>
  );
}
